import React, { FC, useState } from 'react';
import { CompetitionsDropdownType, IMultisportCompetition, Languages, SportTypes } from '../../../../../packages/types';
import { CompetitionGroup, SportsGroup } from '.';
import { DropdownListWrapper } from '@velinsi/smp-widgets-ui/src/molecules/DropdownListWrapper';
import { getLabelValue } from '@sportal365/smp-widget-utils';
import { SMPContainer, SMPFlex } from '@velinsi/smp-widgets-ui';
import { IMultisportEventsLabels } from '../../utils/getMultisportLabels';
import { isSameDate } from './MultisportDropdowns';
import { formatDate } from '../../../../../packages/utils/src/formatDate';

interface CompetitionsDropdownProps {
  data: IMultisportCompetition[];
  onChange: (value: CompetitionsDropdownType) => void;
  selectedCompetition?: CompetitionsDropdownType;
  topEventsOption: CompetitionsDropdownType;
  isOpen?: boolean;
  toggleDropdown: VoidFunction;
  dates: Date[];
  selectedDate: Date;
  handleDateSelect: (option: Date) => void;
  labels: IMultisportEventsLabels;
  showTopEventsAndCompetitionsOptions?: boolean;
  handleTabSelectionClick: () => void;
  localeLang: Languages;
}

export const CompetitionsDropdown: FC<CompetitionsDropdownProps> = ({
  data,
  onChange,
  topEventsOption,
  isOpen,
  toggleDropdown,
  dates,
  selectedDate,
  handleDateSelect,
  selectedCompetition,
  labels,
  showTopEventsAndCompetitionsOptions,
  handleTabSelectionClick,
  localeLang,
}) => {
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const handleChange = (value: CompetitionsDropdownType) => {
    onChange(value);
    toggleDropdown();
  };

  const handleDateChange = (option: Date) => {
    handleDateSelect(option);
    toggleDropdown();
    handleTabSelectionClick();
  };

  return (
    <DropdownListWrapper isOpen={isOpen} css={{ marginRight: '$6_5' }}>
      <SMPFlex
        style={{
          width: '100%',
          gap: '12px',
          padding: '12px 8px',
          borderBottom: showTopEventsAndCompetitionsOptions ? '1px solid var(--colors-gray2)' : 'none',
          alignItems: 'center',
          justifyContent: 'start',
        }}
      >
        {dates.map((option, index) => (
          <SMPContainer
            key={index}
            onClick={() => handleDateChange(option)}
            onMouseOver={() => setHoveredDate(option)}
            onMouseLeave={() => setHoveredDate(null)}
            style={{
              margin: 0,
              padding: '6px 12px',
              borderRadius: '24px',
              fontSize: '12px',
              lineHeight: '20px',
              textTransform: 'capitalize',
              border: '1px solid var(--colors-pillBorderColor)',
              color: !isSameDate(option, selectedDate) || hoveredDate === option ? 'var(--colors-pillTextColor)' : 'var(--colors-gray0)',
              backgroundColor:
                hoveredDate === option
                  ? 'var(--colors-multiSportHoverColor)'
                  : isSameDate(option, selectedDate)
                  ? 'var(--colors-dropdownActiveDateBgColor)'
                  : 'var(--colors-filtersContainerPrimaryBackgroundColor)',
              cursor: 'pointer',
            }}
          >
            {formatDate({ date: option, locale: localeLang })}
          </SMPContainer>
        ))}
      </SMPFlex>
      <SMPContainer style={{ padding: '0 0 0 8px' }}>
        {showTopEventsAndCompetitionsOptions ? (
          <CompetitionGroup
            onClick={() => handleChange(topEventsOption)}
            name={getLabelValue(topEventsOption.label, 'Top Events')}
            isSelected={selectedCompetition?.value === topEventsOption.value}
            topEvents
          />
        ) : (
          <SMPFlex
            align='center'
            justify='spaceBetween'
            style={{
              width: '276px',
            }}
          />
        )}

        {showTopEventsAndCompetitionsOptions
          ? data.map((sportGroup) => {
              if (sportGroup.sport === SportTypes.ALL) return null;
              return (
                <React.Fragment key={sportGroup.sport}>
                  <SportsGroup sportGroup={sportGroup} labels={labels} />
                  {sportGroup.competitions.map((compEntry) => (
                    <CompetitionGroup
                      onClick={() =>
                        handleChange({
                          value: compEntry.competition.id,
                          label: compEntry.competition.name,
                          sport: compEntry.competition.sport as SportTypes,
                          logo: compEntry.competition.logoUrl,
                        })
                      }
                      isSelected={selectedCompetition?.value === compEntry.competition.id}
                      name={compEntry.competition.name}
                      eventCount={compEntry.eventCount}
                      logo={compEntry.competition.logoUrl}
                      key={compEntry.competition.id}
                    />
                  ))}
                </React.Fragment>
              );
            })
          : undefined}
      </SMPContainer>
    </DropdownListWrapper>
  );
};
